import Vue, { watchEffect, Ref, ref } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import JobHistory from '@/models/job-history'
import { notEmpty } from '../utils/notEmpty'
import { getJobStopById } from '@/api/jobs'
import { ActualStop } from '@ironsight/types'
import { changeTypes } from './ChangeTypes'

export function useJobStops(changesRef: Ref<JobHistory[]>) {
  const knownJobStops = ref<Record<string, ActualStop>>({})
  const queryClient = useQueryClient()

  watchEffect(async () => {
    const deletedStops = changesRef.value
      .filter((change) => change.type === changeTypes.ACTUAL_STOP_DELETED)
      .flatMap((change) => change.fieldsChanged)
      .map((field) => field.originalValue)
    const stopsToFetch = changesRef.value.flatMap((change) => {
      return change.fieldsChanged
        .flatMap((field) => {
          if (field.columnName === 'inventory_receipt_id') {
            return [field.originalValue?.stop_id, field.newValue?.stop_id] as (
              | string
              | null
              | undefined
            )[]
          }
          return []
        })
        .filter(notEmpty)
        .filter((stopId) => !deletedStops.includes(stopId))
    })

    const uniqueStopIds = [...new Set(stopsToFetch)].filter((id) => id && !knownJobStops.value[id])

    const stopPromises = uniqueStopIds.map((id) =>
      queryClient
        .fetchQuery({
          queryKey: ['stops', 'byId', id],
          queryFn: async ({ signal }) => getJobStopById(id, signal),
          staleTime: 600000, // 10 minutes
        })
        .then((stops) => {
          return { id, stops }
        }),
    )

    const results = await Promise.all(stopPromises)

    results.forEach(({ id, stops }) => {
      Vue.set(knownJobStops.value, id, stops)
    })
  })

  return {
    knownJobStops,
  }
}
